import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';

const UserManagement = () => {
  const { locationId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userSessions, setUserSessions] = useState([]);
  const [locationInfo, setLocationInfo] = useState(null);
  const [sharingMode, setSharingMode] = useState('INDIVIDUAL');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [agencyId, setAgencyId] = useState(null);
  const [initializingUser, setInitializingUser] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [togglingUser, setTogglingUser] = useState(null);

  useEffect(() => {
    fetchUserInfo();
    fetchAgencyAndSessions();
  }, [locationId]);

  const fetchUserInfo = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch('/auth/profile', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserInfo(data);
      }
    } catch (err) {
      console.error('Error fetching user info:', err);
    }
  };

  const fetchAgencyAndSessions = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      // First fetch current user's agency
      const agencyResponse = await fetch('/agencies/current', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (agencyResponse.ok) {
        const agencyData = await agencyResponse.json();
        console.log('Agency response data:', agencyData);
        
        // Agency ID is nested in agency.id from getAgencyStats response
        const extractedAgencyId = agencyData.agency?.id || agencyData.id;
        console.log('Agency ID loaded:', extractedAgencyId);
        
        if (!extractedAgencyId) {
          setError('Unable to load agency information');
          return;
        }
        
        setAgencyId(extractedAgencyId);
      } else {
        console.error('Failed to fetch agency:', agencyResponse.status);
        setError('Failed to load agency information.');
        setTimeout(() => setError(''), 5000);
      }

      // Then fetch user sessions
      await fetchUserSessions();
    } catch (err) {
      console.error('Error fetching agency:', err);
      setError('Network error loading agency.');
      setTimeout(() => setError(''), 5000);
    }
  };

  const fetchUserSessions = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`/whatsapp/sessions/${locationId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUserSessions(data.userSessions || []);
        // Normalize sharing mode to uppercase for consistency
        const mode = (data.sharingMode || 'SHARED').toUpperCase();
        setSharingMode(mode);
        console.log('Sharing mode loaded:', mode);
        setLocationInfo({
          locationId: data.locationId,
          total: data.total,
          connected: data.connected,
        });
        setError('');
      } else if (response.status === 403) {
        setError('Unauthorized to view this location');
      } else {
        setError('Failed to fetch user sessions');
      }
    } catch (err) {
      console.error('Network error:', err);
      setError('Network error while fetching user sessions');
    } finally {
      setLoading(false);
    }
  };

  const initializeUser = async (userId) => {
    try {
      setInitializingUser(userId);
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`/whatsapp/init-user/${locationId}/${userId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setSuccessMessage('WhatsApp initialization started. Redirecting to QR code...');
        // Redirect to user-specific QR code page
        setTimeout(() => {
          navigate(`/user-whatsapp/${locationId}/${userId}`);
        }, 1000);
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to initialize WhatsApp');
        setTimeout(() => setError(''), 5000);
        setInitializingUser(null);
      }
    } catch (err) {
      setError('Network error while initializing WhatsApp');
      setTimeout(() => setError(''), 5000);
      setInitializingUser(null);
    }
  };

  const disconnectUser = async (userId) => {
    if (!window.confirm('Are you sure you want to disconnect this user\'s WhatsApp? They will need to scan the QR code again.')) {
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`/whatsapp/disconnect-user/${locationId}/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setSuccessMessage('WhatsApp disconnected successfully');
        setTimeout(() => setSuccessMessage(''), 5000);
        fetchUserSessions();
        setInitializingUser(null);
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to disconnect WhatsApp');
        setTimeout(() => setError(''), 5000);
      }
    } catch (err) {
      setError('Network error while disconnecting WhatsApp');
      setTimeout(() => setError(''), 5000);
    }
  };

  const setDefaultSession = async (userId) => {
    try {
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`/whatsapp/set-default/${locationId}/${userId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setSuccessMessage('Default session updated');
        setTimeout(() => setSuccessMessage(''), 5000);
        fetchUserSessions();
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to set default session');
        setTimeout(() => setError(''), 5000);
      }
    } catch (err) {
      setError('Network error while setting default session');
      setTimeout(() => setError(''), 5000);
    }
  };

  const viewQRCode = (userId) => {
    const qrUrl = `/user-whatsapp/${locationId}/${userId}`;
    navigate(qrUrl);
  };

  const toggleSharingMode = async () => {
    console.log('=== Toggle Sharing Mode ===');
    console.log('Current sharing mode:', sharingMode);
    console.log('Current agencyId:', agencyId);
    console.log('Current locationId:', locationId);
    
    // Check if agencyId is loaded
    if (!agencyId) {
      setError('Loading agency information. Please wait...');
      await fetchAgencyAndSessions();
      setTimeout(() => setError(''), 3000);
      return;
    }

    // Toggle between SHARED and INDIVIDUAL (both uppercase)
    const newMode = sharingMode === 'SHARED' ? 'INDIVIDUAL' : 'SHARED';
    const confirmMessage = newMode === 'INDIVIDUAL' 
      ? 'Switch to Individual mode? Each user will need their own WhatsApp connection.'
      : 'Switch to Shared mode? One WhatsApp number will be used for all users.';
    
    if (!window.confirm(confirmMessage)) {
      console.log('User cancelled mode switch');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const payload = { whatsappSharingMode: newMode.toLowerCase() };
      
      console.log('Sending PATCH request to:', `/agencies/${agencyId}/locations/${locationId}`);
      console.log('Payload:', payload);
      
      // Send lowercase to backend
      const response = await fetch(`/agencies/${agencyId}/locations/${locationId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      console.log('Response status:', response.status);
      const data = await response.json();
      console.log('Response data:', data);

      if (response.ok) {
        setSharingMode(newMode);
        setSuccessMessage(`Sharing mode changed to ${newMode}`);
        setTimeout(() => setSuccessMessage(''), 5000);
        fetchUserSessions();
      } else {
        setError(data.message || `Failed to update sharing mode: ${response.status}`);
        setTimeout(() => setError(''), 5000);
      }
    } catch (err) {
      console.error('Error toggling sharing mode:', err);
      setError('Network error while updating sharing mode');
      setTimeout(() => setError(''), 5000);
    }
  };

  const refreshUsers = async () => {
    console.log('Refresh users - agencyId:', agencyId);
    
    if (!agencyId) {
      setError('Loading agency information. Please wait...');
      // Try to fetch agency first
      await fetchAgencyAndSessions();
      setTimeout(() => setError(''), 3000);
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      
      setSuccessMessage('Refreshing users from GoHighLevel...');
      
      const response = await fetch(`/agencies/${agencyId}/locations/${locationId}/refresh-users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSuccessMessage(`Users refreshed! Synced: ${data.synced}, Removed: ${data.removed}`);
        setTimeout(() => setSuccessMessage(''), 5000);
        fetchUserSessions();
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to refresh users');
        setTimeout(() => setError(''), 5000);
      }
    } catch (err) {
      setError('Network error while refreshing users');
      setTimeout(() => setError(''), 5000);
    }
  };

  const toggleUserActive = async (userId, currentIsActive) => {
    if (!agencyId) {
      setError('Loading agency information. Please wait...');
      await fetchAgencyAndSessions();
      setTimeout(() => setError(''), 3000);
      return;
    }

    const confirmMessage = currentIsActive
      ? 'Deactivate this user? They will no longer be charged and cannot use WhatsApp.'
      : 'Activate this user? They will be charged $10/month (first user is free per location).';
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setTogglingUser(userId);
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`/agencies/${agencyId}/locations/${locationId}/users/${userId}/toggle-active`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSuccessMessage(data.message || 'User status updated successfully');
        setTimeout(() => setSuccessMessage(''), 5000);
        fetchUserSessions();
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to update user status');
        setTimeout(() => setError(''), 5000);
      }
    } catch (err) {
      setError('Network error while updating user status');
      setTimeout(() => setError(''), 5000);
    } finally {
      setTogglingUser(null);
    }
  };

  const activateAllUsers = async () => {
    if (!agencyId) {
      setError('Loading agency information. Please wait...');
      await fetchAgencyAndSessions();
      setTimeout(() => setError(''), 3000);
      return;
    }

    const inactiveCount = userSessions.filter(u => !u.isActive).length;
    
    if (inactiveCount === 0) {
      setError('All users are already active');
      setTimeout(() => setError(''), 3000);
      return;
    }

    const confirmMessage = `Activate all ${inactiveCount} inactive user(s)? They will be charged $10/month each (first user is free per location).`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`/agencies/${agencyId}/locations/${locationId}/users/activate-all`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSuccessMessage(data.message || 'All users activated successfully');
        setTimeout(() => setSuccessMessage(''), 5000);
        fetchUserSessions();
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to activate users');
        setTimeout(() => setError(''), 5000);
      }
    } catch (err) {
      setError('Network error while activating users');
      setTimeout(() => setError(''), 5000);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'connected': { color: 'bg-green-100 text-green-800', text: 'Connected' },
      'disconnected': { color: 'bg-gray-100 text-gray-800', text: 'Disconnected' },
      'pending': { color: 'bg-yellow-100 text-yellow-800', text: 'Pending' },
      'error': { color: 'bg-red-100 text-red-800', text: 'Error' },
    };
    
    const badge = badges[status?.toLowerCase()] || badges['disconnected'];
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${badge.color}`}>
        {badge.text}
      </span>
    );
  };

  if (loading) {
    return (
      <DashboardLayout userInfo={userInfo}>
        <div className="flex justify-center items-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    navigate('/login');
  };

  return (
    <DashboardLayout userInfo={userInfo} onLogout={handleLogout}>
      <div className="px-4 py-8">
        <div className="mb-6">
          <button
            onClick={() => navigate('/dashboard/subaccounts')}
            className="text-blue-600 hover:text-blue-800 flex items-center gap-2"
          >
            ← Back to Locations
          </button>
        </div>

      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h1 className="text-3xl font-bold mb-2">User WhatsApp Management</h1>
            <p className="text-gray-600">
              Location: {locationInfo?.locationId}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={activateAllUsers}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
            >
              ✅ Activate All Users
            </button>
            <button
              onClick={refreshUsers}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
            >
              🔄 Refresh Users
            </button>
          </div>
        </div>
        
        <div className="flex gap-4 mb-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex-1">
            <div className="text-sm text-blue-600 font-semibold">Total Users</div>
            <div className="text-2xl font-bold text-blue-900">{locationInfo?.total || 0}</div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex-1">
            <div className="text-sm text-green-600 font-semibold">Connected</div>
            <div className="text-2xl font-bold text-green-900">{locationInfo?.connected || 0}</div>
          </div>
          <div 
            className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex-1 cursor-pointer hover:bg-purple-100 transition-colors"
            onClick={toggleSharingMode}
            title="Click to change sharing mode"
          >
            <div className="text-sm text-purple-600 font-semibold flex items-center justify-between">
              <span>Sharing Mode</span>
              <span className="text-xs">🔄 Click to switch</span>
            </div>
            <div className="text-2xl font-bold text-purple-900">{sharingMode}</div>
          </div>
        </div>

        {sharingMode === 'SHARED' && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-yellow-800">
              <strong>Shared Mode:</strong> One WhatsApp number is used by all users.
            </p>
          </div>
        )}

        {sharingMode === 'INDIVIDUAL' && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-blue-800">
              <strong>Individual Mode:</strong> Each user has their own WhatsApp number. 
              Messages are routed based on conversation history.
            </p>
          </div>
        )}

        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
          <p className="text-sm text-green-800">
            <strong>💰 User Billing:</strong> First user is FREE per location. Additional active users are charged $10/month each.
            Toggle users inactive to stop billing. Only active users can use WhatsApp.
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {successMessage}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Active/Billing
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                WhatsApp Number
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              {sharingMode === 'SHARED' && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Default
                </th>
              )}
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {userSessions.length === 0 ? (
              <tr>
                <td colSpan={sharingMode === 'SHARED' ? 7 : 6} className="px-6 py-4 text-center text-gray-500">
                  No users found for this location
                </td>
              </tr>
            ) : (
              userSessions.map((user) => (
                <tr key={user.userId} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{user.name}</div>
                    <div className="text-sm text-gray-500">{user.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-800">
                      {user.role || 'user'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {togglingUser === user.userId ? (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        <span className="text-xs text-blue-600">Updating...</span>
                      </div>
                    ) : (
                      <button
                        onClick={() => toggleUserActive(user.userId, user.isActive)}
                        className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${
                          user.isActive 
                            ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        title={user.isActive ? 'Click to deactivate (stop billing)' : 'Click to activate (start billing)'}
                      >
                        {user.isActive ? '✓ Active' : '✗ Inactive'}
                      </button>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {user.session?.whatsappNumber || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(user.session?.status || 'disconnected')}
                  </td>
                  {sharingMode === 'SHARED' && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.session?.isDefault ? (
                        <span className="text-green-600 font-semibold">✓ Default</span>
                      ) : (
                        <button
                          onClick={() => setDefaultSession(user.userId)}
                          className="text-blue-600 hover:text-blue-800 text-sm"
                          disabled={!user.session || user.session.status !== 'connected'}
                        >
                          Set Default
                        </button>
                      )}
                    </td>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex gap-2 flex-wrap">
                      {/* Show loading state when initializing */}
                      {initializingUser === user.userId ? (
                        <div className="flex items-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                          <span className="text-sm text-blue-600">Initializing...</span>
                        </div>
                      ) : (
                        <>
                          {/* Show Initialize button when not connected */}
                          {(!user.client?.connected && user.session?.status !== 'connected') && (
                            <button
                              onClick={() => initializeUser(user.userId)}
                              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs font-semibold"
                            >
                              🔗 Initialize
                            </button>
                          )}
                          
                          {/* Show Disconnect button when connected */}
                          {(user.client?.connected || user.session?.status === 'connected') && (
                            <button
                              onClick={() => disconnectUser(user.userId)}
                              className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-xs font-semibold"
                            >
                              🔌 Disconnect
                            </button>
                          )}
                          
                          {/* Show View QR button when QR is available or client is initializing */}
                          {(user.client?.hasQR || user.session?.status === 'pending') && (
                            <button
                              onClick={() => viewQRCode(user.userId)}
                              className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-xs font-semibold"
                            >
                              📱 View QR
                            </button>
                          )}
                          
                          {/* Show QR Link button for public link */}
                          <button
                            onClick={() => window.open(`/whatsapp/qr-latest/${locationId}/${user.userId}`, '_blank')}
                            className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded text-xs font-semibold"
                            title="Open public QR code link"
                          >
                            🔗 QR Link
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
    </DashboardLayout>
  );
};

export default UserManagement;
